import { rgba } from 'polished';
import { useEffect, useState } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import VoiceMemoButton from './VoiceMemoButton';
import VoiceMemoCountdown from './VoiceMemoCountdown';
import VoiceMemoRecorder from './VoiceMemoRecorder';

const Container = styled(Box)<{ active?: boolean; recording?: boolean }>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	transition: 1s cubic-bezier(0, 1, 0, 1) all;
	transform: translateX(-40px);

	${(props) =>
		props.active
			? `
    background: ${rgba(
			props.recording ? props.theme.colors.danger : props.theme.colors.primary,
			0.1,
		)};
    transform: none;
    width: 100%;
    height: 100%;
    border-radius: 8px;
`
			: props.recording &&
				`
        background: ${props.theme.colors.danger};
`}
`;

export default function VoiceMemoOverlay(props: {
	onStart: () => void;
	onError: () => void;
	onFinish: (voiceMemo: { blob: Blob; samples: number[]; sampleRate: number }) => void;
}) {
	const [loading, setLoading] = useState(false);
	const [countdown, setCountdown] = useState(false);
	const [active, setActive] = useState(false);
	const [recording, setRecording] = useState(false);
	const [stream, setStream] = useState<MediaStream | undefined>();

	useEffect(() => {
		if (!countdown) {
			return;
		}

		setTimeout(() => {
			props.onStart();
			setActive(true);
			setRecording(true);
			setCountdown(false);
		}, 3000);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countdown]);

	useEffect(() => {
		setLoading(true);

		(async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				setCountdown(true);
				setLoading(false);
				setStream(stream);
			} catch {
				props.onError();
				setCountdown(false);
				setLoading(false);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!stream) return;
		return () => {
			stream.getTracks().forEach((track) => track.stop());
		};
	}, [stream]);

	return (
		<Container active={active} recording={countdown || recording}>
			{active ? (
				<VoiceMemoRecorder
					stream={stream}
					onFinish={(blob, samples, sampleRate) => props.onFinish({ blob, samples, sampleRate })}
					onError={() => {
						props.onError();
						setActive(false);
						setRecording(false);
						setStream(undefined);
					}}
				/>
			) : countdown ? (
				<VoiceMemoCountdown />
			) : (
				<VoiceMemoButton loading={loading} />
			)}
		</Container>
	);
}
